import { v5 as uuidv5 } from "uuid";

import {
  AnyElement,
  ElementsState,
  ExtendedElementTypes,
  JSXElement,
  RuleStyles,
  StyleBinding,
} from "../canvas/elements.ts";
import {
  PHOTO_ATTRIBUTION_PROP_NAMES,
  getPhotoAttributionProps,
} from "../image/image.ts";

import {
  getElementImageUrls,
  getElementVariantImageUrls,
  mapJsxElementAssetValues,
  parseFlowstepAssetUrl,
} from "./flowstepAsset.ts";

type ImageAliasSource = "upload" | "unsplash";

export type AliasedImage = {
  url: string;
  props?: Readonly<Record<string, string>>;
};

type ImageIdentity = { source: ImageAliasSource; key: string };

const IMAGE_ALIAS_NAMESPACE = "3b1f6c2e-9d4a-5e7b-8c1d-2a6f0e9b4c73";
const IMAGE_ALIAS_HASH_LENGTH = 8;
const IMAGE_ALIAS = `(?:upload|unsplash):[0-9a-f]{${IMAGE_ALIAS_HASH_LENGTH}}`;
const IMAGE_ALIAS_PATTERN = new RegExp(`^${IMAGE_ALIAS}$`);
const IMAGE_ALIASES_IN_MARKUP = new RegExp(`\\b${IMAGE_ALIAS}\\b`, "g");
const URLS_IN_MARKUP = /https?:\/\/[^\s"'`()<>]+/g;
const UNSPLASH_HOST = "images.unsplash.com";

export const isImageAlias = (value: string | undefined): value is string =>
  typeof value === "string" && IMAGE_ALIAS_PATTERN.test(value);

// Every size and search of one Unsplash photo shares its path.
const imageIdentityOf = (url: string): ImageIdentity | null => {
  const reference = parseFlowstepAssetUrl(url);
  if (reference !== null) {
    return { source: "upload", key: reference };
  }

  if (!URL.canParse(url)) {
    return null;
  }
  const { hostname, pathname } = new URL(url);
  return hostname === UNSPLASH_HOST
    ? { source: "unsplash", key: pathname }
    : null;
};

const aliasFor = ({ source, key }: ImageIdentity): string =>
  `${source}:${uuidv5(key, IMAGE_ALIAS_NAMESPACE).slice(0, IMAGE_ALIAS_HASH_LENGTH)}`;

/** Aliases derive from the image, so they hold across requests; this only knows which ones the current request can name. */
export type ImageAliases = Map<
  string,
  AliasedImage & { identity: ImageIdentity }
>;

export const createImageAliases = (): ImageAliases => new Map();

export const addImageAlias = (
  imageAliases: ImageAliases,
  image: AliasedImage,
): string | null => {
  const identity = imageIdentityOf(image.url);
  if (identity === null) {
    return null;
  }

  const alias = aliasFor(identity);
  const known = imageAliases.get(alias);
  if (known === undefined) {
    imageAliases.set(alias, { ...image, identity });
    return alias;
  }

  if (known.identity.key !== identity.key) {
    console.error("Image alias collision; showing the url instead", {
      alias,
      known: known.url,
      url: image.url,
    });
    return null;
  }

  if (!known.props && image.props) {
    imageAliases.set(alias, { ...known, props: image.props });
  }
  return alias;
};

export const addImageAliasesFromElements = (
  imageAliases: ImageAliases,
  elements: Iterable<AnyElement>,
): void => {
  for (const element of elements) {
    if (element.type !== ExtendedElementTypes.JSXElement) {
      continue;
    }

    for (const url of getElementImageUrls(element)) {
      const props =
        url === element.props?.src
          ? getPhotoAttributionProps(element.props)
          : {};
      addImageAlias(imageAliases, {
        url,
        ...(Object.keys(props).length > 0 && { props }),
      });
    }
    for (const url of getElementVariantImageUrls(element)) {
      addImageAlias(imageAliases, { url });
    }
  }
};

export const imageAliasOf = (
  imageAliases: ImageAliases,
  url: string,
): string | undefined => {
  const identity = imageIdentityOf(url);
  if (identity === null) {
    return undefined;
  }

  const alias = aliasFor(identity);
  return imageAliases.get(alias)?.identity.key === identity.key
    ? alias
    : undefined;
};

export const resolveImageAlias = (
  imageAliases: ImageAliases,
  alias: string,
): AliasedImage | undefined => {
  const image = imageAliases.get(alias);
  return (
    image && { url: image.url, ...(image.props && { props: image.props }) }
  );
};

export const bakeImageAliasesInMarkup = (
  imageAliases: ImageAliases,
  markup: string,
): string =>
  markup.replace(
    IMAGE_ALIASES_IN_MARKUP,
    (alias) => resolveImageAlias(imageAliases, alias)?.url ?? alias,
  );

export const unbakeImageAliasesInMarkup = (
  imageAliases: ImageAliases,
  markup: string,
): string =>
  markup.replace(
    URLS_IN_MARKUP,
    (url) => addImageAlias(imageAliases, { url }) ?? url,
  );

export const unbakeFlowstepAssetUrls = <T extends ElementsState>(
  elements: T,
  imageAliases: ImageAliases,
): T => {
  addImageAliasesFromElements(imageAliases, Object.values(elements));
  const unbaked: ElementsState = { ...elements };

  for (const [elementId, element] of Object.entries(elements)) {
    if (element.type !== ExtendedElementTypes.JSXElement) {
      continue;
    }

    const mapped = mapJsxElementAssetValues(element, (value) =>
      typeof value === "string" ? imageAliasOf(imageAliases, value) : undefined,
    );
    unbaked[elementId] = isImageAlias(mapped.props?.src)
      ? {
          ...mapped,
          props: Object.fromEntries(
            Object.entries(mapped.props).filter(
              ([propName]) => !PHOTO_ATTRIBUTION_PROP_NAMES.includes(propName),
            ),
          ),
        }
      : mapped;
  }

  return unbaked as T;
};

/**
 * The bindings at `backgroundFills.<n>` renumbered for the fills `kept` (their
 * old indexes, in order); a binding on a fill that went goes with it.
 */
const renumberedFills = (
  bindings: StyleBinding[] | undefined,
  kept: number[],
): StyleBinding[] | undefined =>
  bindings?.flatMap((binding) => {
    const match = /^backgroundFills\.(\d+)(\..*)?$/.exec(binding.target);
    if (match === null) return [binding];
    const at = kept.indexOf(Number(match[1]));
    return at === -1
      ? []
      : [{ ...binding, target: `backgroundFills.${at}${match[2] ?? ""}` }];
  });

const withoutStylesImages = <S extends RuleStyles>(
  styles: S,
  bindings: StyleBinding[] | undefined,
  isDropped: (value: string | undefined) => boolean,
): { styles: S; bindings: StyleBinding[] | undefined } => {
  const kept = (styles.backgroundFills ?? []).flatMap((fill, index) =>
    fill.kind !== "image" || !isDropped(fill.source) ? [index] : [],
  );
  const fills = styles.backgroundFills?.filter((_, index) =>
    kept.includes(index),
  );
  const dropMask = isDropped(styles.mask?.image);
  const dropListStyleImage = isDropped(styles.listStyleImage);

  if (
    fills?.length === styles.backgroundFills?.length &&
    !dropMask &&
    !dropListStyleImage
  ) {
    return { styles, bindings };
  }

  const { listStyleImage: _listStyleImage, ...withoutListStyleImage } = styles;
  const { image: _image, ...mask } = styles.mask ?? {};
  // The same kind of styles back: only image fields change.
  return {
    styles: {
      ...(dropListStyleImage ? withoutListStyleImage : styles),
      ...(fills && { backgroundFills: fills }),
      ...(dropMask && { mask }),
    } as S,
    bindings: renumberedFills(bindings, kept),
  };
};

const withoutImages = (
  element: JSXElement,
  isDropped: (value: string | undefined) => boolean,
): JSXElement => {
  const base =
    element.styles &&
    withoutStylesImages(element.styles, element.styleBindings, isDropped);
  const variants = element.variants?.map((variant) => {
    const { styles, bindings } = withoutStylesImages(
      variant.styles,
      variant.styleBindings,
      isDropped,
    );
    if (styles === variant.styles) return variant;
    const { styleBindings: _styleBindings, ...rest } = variant;
    return {
      ...rest,
      styles,
      ...(bindings !== undefined &&
        bindings.length > 0 && {
          styleBindings: bindings,
        }),
    };
  });
  const variantsChanged =
    variants !== undefined &&
    variants.some((variant, index) => variant !== element.variants?.[index]);

  if (base?.styles === element.styles && !variantsChanged) return element;
  const { styleBindings: _styleBindings, ...rest } = element;
  const bindings = base === undefined ? element.styleBindings : base.bindings;
  return {
    ...rest,
    styles: base?.styles ?? element.styles,
    ...(bindings !== undefined &&
      bindings.length > 0 && {
        styleBindings: bindings,
      }),
    ...(variantsChanged && { variants }),
  };
};

const collectSubtrees = (
  elements: ElementsState,
  rootIds: string[],
): Set<string> => {
  const collected = new Set<string>();
  const pending = [...rootIds];
  for (let elementId = pending.pop(); elementId; elementId = pending.pop()) {
    if (collected.has(elementId)) {
      continue;
    }
    collected.add(elementId);
    const element = elements[elementId];
    if (element && "children" in element) {
      pending.push(...element.children);
    }
  }
  return collected;
};

/** An alias this request cannot name drops its image — the element it is the `src` of, or a style's image — rather than show something the model made up. */
export const bakeFlowstepAssetUrls = <T extends ElementsState>(
  elements: T,
  imageAliases: ImageAliases | undefined,
): T => {
  if (!imageAliases) {
    return elements;
  }

  const isUnknown = (value: string | undefined): boolean =>
    isImageAlias(value) && resolveImageAlias(imageAliases, value) === undefined;

  const unresolved = Object.values(elements).filter(
    (element): element is JSXElement =>
      element.type === ExtendedElementTypes.JSXElement &&
      isUnknown(element.props?.src),
  );
  if (unresolved.length > 0) {
    console.warn("Unknown image alias; leaving the image out", {
      aliases: unresolved.map((element) => element.props.src),
      known: [...imageAliases.keys()],
    });
  }
  const dropped = collectSubtrees(
    elements,
    unresolved.map((element) => element.id),
  );

  const baked: ElementsState = {};
  for (const [elementId, element] of Object.entries(elements)) {
    if (dropped.has(elementId)) {
      continue;
    }
    if (element.type !== ExtendedElementTypes.JSXElement) {
      baked[elementId] = element;
      continue;
    }

    const children = element.children.some((id) => dropped.has(id))
      ? element.children.filter((id) => !dropped.has(id))
      : element.children;
    const resolved = mapJsxElementAssetValues(
      withoutImages(
        children === element.children ? element : { ...element, children },
        isUnknown,
      ),
      (value) =>
        typeof value === "string" && isImageAlias(value)
          ? resolveImageAlias(imageAliases, value)?.url
          : undefined,
    );

    const src = element.props?.src;
    const image = isImageAlias(src)
      ? resolveImageAlias(imageAliases, src)
      : undefined;
    baked[elementId] = image?.props
      ? { ...resolved, props: { ...resolved.props, ...image.props } }
      : resolved;
  }

  return baked as T;
};
